<template>
  <div class="cl-panel-stack">
    <article class="card">
      <header class="cl-card-head">
        <div>
          <p class="eyebrow">Deaths</p>
          <h3>{{ plural(deaths.length, 'player death') }}</h3>
          <p class="muted-line">
            Each death lists the events of the last {{ windowSeconds }} seconds in chronological order. These are the logged
            events only, with no interpretation of who or what was at fault.
          </p>
        </div>
        <div class="page-actions">
          <AppDropdown
            size="sm"
            label="Window"
            align="right"
            :model-value="windowSeconds"
            :options="windowOptions"
            @update:model-value="$emit('setWindow', $event)"
          />
        </div>
      </header>

      <p v-if="!deaths.length" class="cl-empty-line">Nobody died in this segment.</p>

      <ul v-else class="cl-death-list">
        <li v-for="(death, index) in deaths" :key="`${death.guid}-${death.ts}`">
          <button class="cl-death-head" type="button" @click="toggle(index)">
            <span class="cl-death-index">{{ index + 1 }}</span>
            <span class="cl-death-title">
              <strong>{{ death.name }}</strong>
              <small>
                {{ formatOffset(death.offsetMs) }} into the segment - {{ formatClock(death.ts) }}
                <template v-if="death.encounterName"> - {{ death.encounterName }}</template>
              </small>
            </span>
            <span class="cl-death-blow">
              <template v-if="death.killingBlow">
                <strong>{{ death.killingBlow.spellName || 'Melee' }}</strong>
                <small>
                  {{ formatNumber(death.killingBlow.amount) }} from {{ death.killingBlow.sourceName || NO_DATA }}
                  <template v-if="death.killingBlow.overkill > 0"> ({{ formatNumber(death.killingBlow.overkill) }} overkill)</template>
                </small>
              </template>
              <template v-else>
                <strong>{{ NO_DATA }}</strong>
                <small>No damage event inside the window</small>
              </template>
            </span>
            <span class="cl-death-toggle">
              <i class="fa-solid" :class="open[index] ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </span>
          </button>

          <section v-if="open[index]" class="cl-death-body">
            <div class="cl-death-stats">
              <div>
                <span>Damage taken in window</span>
                <strong>{{ formatNumber(stats(death).damage) }}</strong>
              </div>
              <div>
                <span>Healing received</span>
                <strong>{{ formatNumber(stats(death).healing) }}</strong>
              </div>
              <div>
                <span>Absorbed</span>
                <strong>{{ formatNumber(stats(death).absorbed) }}</strong>
              </div>
              <div>
                <span>Health at last update</span>
                <strong>{{ death.maxHp ? `${formatNumber(death.hp)} / ${formatNumber(death.maxHp)}` : NO_DATA }}</strong>
              </div>
              <div>
                <span>Events recorded</span>
                <strong>{{ windowEvents(death).length }}</strong>
              </div>
            </div>

            <div class="cl-death-defensives">
              <h4>Defensive cooldowns</h4>
              <ul v-if="defensivesAtDeath(death).length" class="cl-chip-list">
                <li
                  v-for="aura in defensivesAtDeath(death)"
                  :key="`def-${aura.spellId}`"
                  class="is-defensive"
                  :title="aura.kindLabel"
                >
                  <i class="fa-solid fa-shield-halved"></i>
                  {{ aura.name }}
                  <small>up for {{ formatDuration(death.ts - aura.appliedAt) }}</small>
                </li>
              </ul>
              <p v-else class="muted-line">
                None of the defensive cooldowns this analyzer knows by spell id were active when the killing blow landed.
                A cooldown it does not know stays unlabelled rather than guessed.
              </p>
              <p v-if="defensivesInWindow(death).length" class="muted-line">
                Pressed during the window: {{ defensivesInWindow(death).map((event) => event.spellName).join(', ') }}.
              </p>
            </div>

            <div v-if="death.activeAuras.length" class="cl-death-auras">
              <h4>Auras active at the time of death</h4>
              <ul class="cl-chip-list">
                <li
                  v-for="aura in death.activeAuras"
                  :key="`${aura.spellId}-${aura.appliedAt}`"
                  :class="aura.auraType === 'BUFF' ? 'is-buff' : 'is-debuff'"
                >
                  {{ aura.name }}
                  <small v-if="aura.stacks > 1">x{{ aura.stacks }}</small>
                  <small>{{ formatDuration(death.ts - aura.appliedAt) }}</small>
                </li>
              </ul>
            </div>
            <p v-else class="muted-line">No aura applications were recorded for this player before the death.</p>

            <h4>Last {{ windowSeconds }} seconds</h4>
            <ol v-if="windowEvents(death).length" class="cl-death-events">
              <li
                v-for="(event, eventIndex) in windowEvents(death)"
                :key="`${event.ts}-${eventIndex}`"
                :class="`is-${event.kind}`"
                :title="event.kind === 'defensive' ? kindLabel(event.defensiveKind) : ''"
              >
                <span class="cl-death-time">-{{ ((death.ts - event.ts) / 1000).toFixed(1) }}s</span>
                <span class="cl-death-icon">
                  <i class="fa-solid" :class="iconFor(event.kind)"></i>
                </span>
                <span class="cl-death-text">
                  <strong>{{ event.spellName || 'Melee' }}</strong>
                  <small>
                    <template v-if="event.kind === 'damage'">
                      {{ formatNumber(event.amount) }} damage from {{ event.sourceName || NO_DATA }}
                      <template v-if="event.absorbed > 0"> ({{ formatNumber(event.absorbed) }} absorbed)</template>
                      <template v-if="event.critical"> - critical</template>
                    </template>
                    <template v-else-if="event.kind === 'heal'">
                      {{ formatNumber(event.amount) }} healing from {{ event.sourceName || NO_DATA }}
                      <template v-if="event.overheal > 0"> ({{ formatNumber(event.overheal) }} overheal)</template>
                    </template>
                    <template v-else-if="event.kind === 'defensive'">
                      <template v-if="!event.applied">Expired or cancelled</template>
                      <template v-else-if="event.selfCast">Used on themselves</template>
                      <template v-else>Cast on them by {{ event.sourceName || NO_DATA }}</template>
                    </template>
                    <template v-else>
                      {{ formatNumber(event.amount) }} absorbed<template v-if="event.sourceName"> by {{ event.sourceName }}</template>
                    </template>
                  </small>
                </span>
                <span class="cl-death-hp">
                  <template v-if="event.maxHp">
                    <span class="cl-hp-track">
                      <span class="cl-hp-fill" :style="{ width: hpPercent(event) + '%' }"></span>
                    </span>
                    <small>{{ hpPercent(event) }}%</small>
                  </template>
                  <small v-else>{{ NO_DATA }}</small>
                </span>
              </li>
            </ol>
            <p v-else class="cl-empty-line">
              No events were recorded in this window. Only the first {{ detailLimit }} deaths of a log keep their full event
              history, to keep the stored analysis small.
            </p>
          </section>
        </li>
      </ul>
    </article>
  </div>
</template>

<script>
import AppDropdown from '../AppDropdown.vue'
import { describeDefensive, DEFENSIVE_KIND_LABELS } from '../../services/combatLog/constants'
import {
  NO_DATA,
  formatNumber,
  formatDuration,
  formatClock,
  formatOffset,
  plural,
} from '../../services/combatLog/format'

export default {
  name: 'CombatLogDeaths',
  components: { AppDropdown },
  props: {
    deaths: { type: Array, default: () => [] },
    windowSeconds: { type: Number, default: 10 },
    detailLimit: { type: Number, default: 300 },
  },
  emits: ['setWindow'],
  data() {
    return { NO_DATA, open: {} }
  },
  computed: {
    windowOptions() {
      return [5, 10, 15, 20].map((seconds) => ({ value: seconds, label: `${seconds} s` }))
    },
  },
  watch: {
    deaths() {
      this.open = {}
    },
  },
  methods: {
    formatNumber,
    formatDuration,
    formatClock,
    formatOffset,
    plural,
    toggle(index) {
      this.open = { ...this.open, [index]: !this.open[index] }
    },
    // The analyzer keeps a 10 second buffer; a shorter window is applied here so
    // changing it does not require re-reading the log.
    windowEvents(death) {
      const from = death.ts - this.windowSeconds * 1000
      return (death.events || []).filter((event) => event.ts >= from)
    },
    stats(death) {
      return this.windowEvents(death).reduce((acc, event) => {
        if (event.kind === 'damage') {
          acc.damage += event.amount || 0
          acc.absorbed += event.absorbed || 0
        } else if (event.kind === 'heal') {
          acc.healing += event.amount || 0
        } else if (event.kind === 'absorb') {
          acc.absorbed += event.amount || 0
        }
        return acc
      }, { damage: 0, healing: 0, absorbed: 0 })
    },
    // Known defensives that were still up when the player died, taken from the
    // aura snapshot rather than from the window, so one applied long before the
    // fight still counts.
    defensivesAtDeath(death) {
      return (death.activeAuras || [])
        .map((aura) => {
          const known = describeDefensive(aura.spellId)
          if (!known) return null
          return { ...aura, kind: known.kind, kindLabel: DEFENSIVE_KIND_LABELS[known.kind] || '' }
        })
        .filter(Boolean)
        .sort((left, right) => left.appliedAt - right.appliedAt)
    },
    defensivesInWindow(death) {
      return this.windowEvents(death).filter((event) => event.kind === 'defensive' && event.applied)
    },
    kindLabel(kind) {
      return DEFENSIVE_KIND_LABELS[kind] || 'Defensive'
    },
    hpPercent(event) {
      if (!event.maxHp) return 0
      return Math.max(0, Math.min(100, Math.round((event.hpAfter / event.maxHp) * 100)))
    },
    iconFor(kind) {
      if (kind === 'heal') return 'fa-plus'
      if (kind === 'absorb') return 'fa-shield-halved'
      if (kind === 'defensive') return 'fa-shield'
      return 'fa-burst'
    },
  },
}
</script>
